import { useEffect, useState, type JSX } from "react";
import { useTranslation } from "react-i18next";

import { apiClient } from "@/api/client";
import { useCan } from "@/shared/auth";
import { PageHeader } from "@/shared/components/PageHeader";
import { Pagination } from "@/shared/components/Pagination";
import { useDensity } from "@/shared/hooks/useDensity";
import { toast } from "@/shared/lib/toast";

import { useListStock } from "../api/use-list-stock";
import { AuditDrawer } from "../components/AuditDrawer";
import { BreakerBanner } from "../components/BreakerBanner";
import { BundlesPanel } from "../components/BundlesPanel";
import { CreateSkuOrBundleModal } from "../components/CreateSkuOrBundleModal";
import { DeleteSKUConfirm } from "../components/DeleteSKUConfirm";
import { EditSKUModal } from "../components/EditSKUModal";
import { InventoryTable } from "../components/InventoryTable";
import { InventoryToolbar, type InventoryTab } from "../components/InventoryToolbar";
import { PushHistoryDrawer } from "../components/PushHistoryDrawer";
import { SellerRefModal } from "../components/SellerRefModal";
import { SyncMultiModal } from "../components/SyncMultiModal";
import { TransferModal } from "../components/TransferModal";
import { UnmappedSkuPanel } from "../components/UnmappedSkuPanel";
import { downloadCsv, stockToCsv } from "../lib/export-stock-csv";
import {
  isSortDir,
  isSortKey,
  type FilterUpdate,
  type InventoryFilterState,
  type SortDir,
  type SortKey,
} from "../lib/url-state";

const DEFAULT_FILTER: InventoryFilterState = {
  search: "",
  offset: 0,
};

interface InventoryPageProps {
  /** When provided (by the route), the URL is the source of truth for filters+sort. */
  urlFilter?: InventoryFilterState;
  onUrlFilterChange?: (next: InventoryFilterState) => void;
}

export function InventoryPage({
  urlFilter,
  onUrlFilterChange,
}: InventoryPageProps = {}): JSX.Element {
  const { t } = useTranslation(["inventory", "nav"]);
  // Optionally controlled: when the route supplies `urlFilter`, the URL is the
  // source of truth; otherwise fall back to internal state (bare-component tests).
  const [internalFilter, setInternalFilter] = useState<InventoryFilterState>(DEFAULT_FILTER);
  const filterState = urlFilter ?? internalFilter;
  const setFilterState = onUrlFilterChange ?? setInternalFilter;
  const [activeTab, setActiveTab] = useState<InventoryTab>("all");
  const [bundleTotal, setBundleTotal] = useState<number | undefined>(undefined);
  const [unmappedTotal, setUnmappedTotal] = useState<number | undefined>(undefined);
  const view: "stock" | "bundles" = activeTab === "bundles" ? "bundles" : "stock";
  const canWriteStock = useCan("stock:write");
  const canWriteListing = useCan("listing:write");
  const [create, setCreate] = useState<{ bundle: boolean } | null>(null);
  const [syncMultiOpen, setSyncMultiOpen] = useState(false);
  const [deleteSku, setDeleteSku] = useState<string | null>(null);
  const [editSku, setEditSku] = useState<{ sku: string; name: string } | null>(null);
  const [transfer, setTransfer] = useState<{ sku: string; mkp: string; name?: string } | null>(
    null,
  );
  const [sellerRef, setSellerRef] = useState<{
    sku: string;
    marketplace: "shopee" | "lazada" | "tiktok";
  } | null>(null);
  const [pushHistory, setPushHistory] = useState<{ sku: string; mkp: string } | null>(null);
  const [audit, setAudit] = useState<{ sku: string; mkp: string; name?: string } | null>(() => {
    const hash = window.location.hash;
    if (hash.startsWith("#audit=")) {
      const [sku, mkp] = hash.slice(7).split("-");
      if (sku && mkp) return { sku, mkp };
    }
    return null;
  });

  // The URL search params pass through TanStack Router unvalidated, so
  // `filterState.sortKey`/`sortDir` may hold arbitrary strings (e.g. from a
  // stale or hand-edited URL). Sanitize against the whitelist before use:
  // invalid → unset, so the API omits `sort_key`/`sort_dir` (BE applies its
  // default) and the table falls back to sku/asc. Never sends an invalid value.
  const safeSortKey: SortKey | undefined = isSortKey(filterState.sortKey)
    ? filterState.sortKey
    : undefined;
  const safeSortDir: SortDir | undefined = isSortDir(filterState.sortDir)
    ? filterState.sortDir
    : undefined;

  // Strip the raw (possibly invalid) sort fields off filterState, then re-add
  // only the sanitized values. Omitting the keys when invalid means the hook
  // forwards no `sort_key`/`sort_dir`, so the BE applies its default sort.
  const { sortKey: _rawSortKey, sortDir: _rawSortDir, ...filterRest } = filterState;
  const filter: InventoryFilterState = {
    ...filterRest,
    ...(safeSortKey !== undefined ? { sortKey: safeSortKey } : {}),
    ...(safeSortDir !== undefined ? { sortDir: safeSortDir } : {}),
  };
  const unmappedView = filterState.brand === "unmapped";
  const { data, isLoading, isError, refetch } = useListStock(filter, !unmappedView);
  const [density, setDensity] = useDensity();

  const PAGE_LIMIT = filterState.limit ?? 50;

  useEffect(() => {
    if (audit) {
      window.location.hash = `#audit=${audit.sku}-${audit.mkp}`;
    } else {
      if (window.location.hash.startsWith("#audit=")) window.location.hash = "";
    }
  }, [audit]);

  function handleFilterChange(updates: FilterUpdate) {
    setFilterState({ ...filterState, ...updates } as InventoryFilterState);
  }

  function handleTabChange(tab: InventoryTab) {
    setActiveTab(tab);
    // Only reset pagination in the URL-backed filter when switching to a stock tab.
    if (tab !== "bundles") {
      handleFilterChange({ offset: 0 });
    }
  }

  function handleSort(key: SortKey) {
    const isActive = (filterState.sortKey ?? "sku") === key;
    const currentDir: SortDir = filterState.sortDir ?? "asc";
    const nextDir: SortDir = isActive && currentDir === "asc" ? "desc" : "asc";
    handleFilterChange({ sortKey: key, sortDir: nextDir, offset: 0 });
  }

  async function handleExportCsv(): Promise<void> {
    const { data: full, error } = await apiClient.GET("/v1/stock", {
      params: {
        query: {
          limit: 10000,
          offset: 0,
          ...(filter.marketplace ? { marketplace: filter.marketplace } : {}),
          ...(filter.status ? { status: filter.status } : {}),
          ...(filter.search ? { search: filter.search } : {}),
        },
      },
    });
    if (error) {
      toast.error(t("toolbar.export_failed"));
      return;
    }
    const items = full?.items ?? [];
    if (items.length === 0) {
      toast.info(t("toolbar.export_empty"));
      return;
    }
    downloadCsv(`inventory-${new Date().toISOString().slice(0, 10)}.csv`, stockToCsv(items));
    toast.success(t("toolbar.export_done", { count: items.length }));
  }

  // sino parity: Single = base stock SKUs, Bundles = listings, All = both.
  const tabCounts = (() => {
    const single = data?.counts?.all ?? data?.total ?? data?.items?.length ?? 0;
    return {
      single,
      all: single + (bundleTotal ?? 0),
      ...(bundleTotal !== undefined ? { bundles: bundleTotal } : {}),
    };
  })();

  const transferOnHand = (() => {
    if (!transfer) return { shopee: 0, lazada: 0, tiktok: 0 };
    const rows = (data?.items ?? []).filter((i) => i.sku === transfer.sku);
    return {
      shopee: rows.find((r) => r.marketplace === "shopee")?.on_hand ?? 0,
      lazada: rows.find((r) => r.marketplace === "lazada")?.on_hand ?? 0,
      tiktok: rows.find((r) => r.marketplace === "tiktok")?.on_hand ?? 0,
    };
  })();

  return (
    <div data-testid="inventory-page">
      <PageHeader
        breadcrumb={
          <>
            <span>{t("nav:group.operations")}</span>
            <span className="text-[var(--premium-border-strong)]">/</span>
            <span className="text-[var(--text-secondary)]">{t("inventory:page.title")}</span>
          </>
        }
        title={t("page.title")}
        subtitle={
          unmappedView
            ? t("unmapped.subtitle", { count: unmappedTotal ?? 0 })
            : t("page.subtitle", { count: data?.total ?? 0 })
        }
      />
      <InventoryToolbar
        filter={filterState}
        onFilterChange={handleFilterChange}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        counts={tabCounts}
        {...((view === "bundles" ? canWriteListing : canWriteStock)
          ? { onCreateSku: () => setCreate({ bundle: view === "bundles" }) }
          : {})}
        density={density}
        onDensityChange={setDensity}
        onExportCsv={() => void handleExportCsv()}
        {...(canWriteStock ? { onSyncMulti: () => setSyncMultiOpen(true) } : {})}
        canImport={canWriteStock}
        hideTabs={unmappedView}
      />
      <BreakerBanner />
      {unmappedView && (
        <UnmappedSkuPanel
          search={filterState.search}
          offset={filterState.offset}
          limit={PAGE_LIMIT}
          onPageChange={({ offset, limit }) => handleFilterChange({ offset, limit })}
          onTotalChange={setUnmappedTotal}
        />
      )}
      {/* Single (base stock). Shown on "single" and merged into "all". */}
      {!unmappedView && activeTab !== "bundles" && (
        <>
          {activeTab === "all" && (
            <h3
              data-testid="all-section-single"
              className="mb-2 mt-1 text-[13px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)]"
            >
              {t("tab.single")}
            </h3>
          )}
          <InventoryTable
            items={data?.items ?? []}
            isLoading={isLoading}
            isError={isError}
            canWriteStock={canWriteStock}
            canWriteListing={canWriteListing}
            onDeleteSku={(sku) => setDeleteSku(sku)}
            onEditSku={(sku) => {
              const row = (data?.items ?? []).find((i) => i.sku === sku);
              setEditSku({ sku, name: row?.product_name ?? "" });
            }}
            onTransfer={(sku, mkp) => {
              setTransfer({ sku, mkp });
            }}
            onAudit={(sku, mkp) => {
              setAudit({ sku, mkp });
            }}
            onCheckSellername={(sku, mkp) => {
              setSellerRef({ sku, marketplace: mkp as "shopee" | "lazada" | "tiktok" });
            }}
            onPushHistory={(sku, mkp) => {
              setPushHistory({ sku, mkp });
            }}
            density={density}
            onClearFilters={() => setFilterState(DEFAULT_FILTER)}
            onRetry={() => void refetch()}
            sortKey={safeSortKey ?? "sku"}
            sortDir={safeSortDir ?? "asc"}
            onSort={handleSort}
          />
          <Pagination
            total={data?.total ?? 0}
            offset={filterState.offset}
            limit={PAGE_LIMIT}
            onChange={({ offset, limit }) => handleFilterChange({ offset, limit })}
          />
        </>
      )}
      {/* Bundles (listings). Shown on "bundles" and merged into "all". */}
      {!unmappedView && activeTab !== "single" && (
        <>
          {activeTab === "all" && (
            <h3
              data-testid="all-section-bundles"
              className="mb-2 mt-6 text-[13px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)]"
            >
              {t("tab.bundles")}
            </h3>
          )}
          <BundlesPanel
            onTotalChange={setBundleTotal}
            canWriteListing={canWriteListing}
            paginated={activeTab === "bundles"}
            search={filterState.search}
            marketplace={filterState.marketplace}
          />
        </>
      )}
      {create && (
        <CreateSkuOrBundleModal defaultBundle={create.bundle} onClose={() => setCreate(null)} />
      )}
      <SyncMultiModal open={syncMultiOpen} onClose={() => setSyncMultiOpen(false)} />
      <PushHistoryDrawer
        open={!!pushHistory}
        onClose={() => setPushHistory(null)}
        sku={pushHistory?.sku ?? ""}
        mkp={pushHistory?.mkp ?? ""}
      />
      {deleteSku && <DeleteSKUConfirm sku={deleteSku} onClose={() => setDeleteSku(null)} />}
      {editSku && (
        <EditSKUModal
          sku={editSku.sku}
          currentName={editSku.name}
          onClose={() => setEditSku(null)}
        />
      )}
      <AuditDrawer
        open={!!audit}
        onClose={() => setAudit(null)}
        sku={audit?.sku ?? ""}
        mkp={audit?.mkp ?? ""}
        {...(audit?.name ? { productName: audit.name } : {})}
      />
      {transfer && (
        <TransferModal
          open={!!transfer}
          onClose={() => setTransfer(null)}
          sku={transfer.sku}
          fromMkp={transfer.mkp as "shopee" | "lazada" | "tiktok"}
          onHandByMkp={transferOnHand}
          {...(transfer.name ? { productName: transfer.name } : {})}
        />
      )}
      {sellerRef && (
        <SellerRefModal
          open={!!sellerRef}
          onClose={() => setSellerRef(null)}
          sku={sellerRef.sku}
          marketplace={sellerRef.marketplace}
        />
      )}
    </div>
  );
}
